/**
 * DailyWorksheet — the student's worksheet for a given school day, built from that
 * day's lesson. Renders as an actual worksheet (paper card): Part A multiple choice,
 * Part B fill-in-the-blank, Part C problem solving. Auto-grades objective items and
 * AI-grades open responses on submit. Includes a "Download PDF" printable version.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Download, Loader2, CheckCircle2, XCircle, Sparkles, RefreshCw, BookOpen } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const LETTERS = ['A', 'B', 'C', 'D', 'E'];

const DailyWorksheet = () => {
  const [loading, setLoading] = useState(true);
  const [ws, setWs] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/school/daily-worksheet', { headers: authHeaders() });
      setWs(r.data.worksheet);
      if (r.data.submission) {
        setResult(r.data.submission);
        setAnswers(r.data.submission.answers || {});
      }
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setAns = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post('/school/daily-worksheet/submit', { date: ws.date, answers }, { headers: authHeaders() });
      setResult(r.data.submission);
      toast.success('Worksheet graded');
    } catch (e) { toast.error('Could not submit'); }
    finally { setSubmitting(false); }
  };

  const retry = () => { setResult(null); setAnswers({}); };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/school/daily-worksheet/pdf?date=${ws.date}`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `PRSVR_Worksheet_${ws.date}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download PDF'); }
    finally { setDownloading(false); }
  };

  if (loading) {
    return (
      <div className="mb-6 border-2 border-zinc-800 px-5 py-4 flex items-center gap-3" data-testid="worksheet-loading">
        <Loader2 className="w-4 h-4 animate-spin text-orange-500" /><span className="text-xs text-zinc-500">Loading today's worksheet…</span>
      </div>
    );
  }
  if (!ws) return null;

  const qs = ws.questions || [];
  const resById = {};
  (result?.results || []).forEach((r) => { resById[r.id] = r; });
  const mc = qs.filter((q) => q.type === 'mc');
  const fb = qs.filter((q) => q.type === 'fill_blank');
  const pr = qs.filter((q) => q.type === 'problem');
  const num = {}; qs.forEach((q, i) => { num[q.id] = i + 1; });

  const QResult = ({ q }) => {
    const r = resById[q.id]; if (!r) return null;
    const ok = r.correct;
    return (
      <div className={`mt-2 text-[12px] px-3 py-2 border-l-4 ${ok ? 'border-emerald-500 bg-emerald-50' : 'border-red-400 bg-red-50'}`} data-testid={`ws-result-${q.id}`}>
        <div className={`font-bold flex items-center gap-1.5 ${ok ? 'text-emerald-700' : 'text-red-600'}`}>
          {ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {q.type === 'problem' ? `Score: ${r.score ?? 0}/100` : (ok ? 'Correct' : 'Not quite')}
        </div>
        {r.feedback && <p className="text-zinc-700 mt-1 leading-snug">{r.feedback}</p>}
        {q.type !== 'problem' && !ok && r.answer && <p className="text-zinc-700 mt-1">Answer: <span className="font-bold">{r.answer}</span></p>}
        {r.explanation && <p className="text-zinc-500 mt-1 italic">{r.explanation}</p>}
      </div>
    );
  };

  return (
    <div className="mb-6 border-2 border-orange-500/40" data-testid="daily-worksheet">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 px-5 py-3 bg-orange-500/[0.05]" data-testid="worksheet-toggle">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-orange-500" />
          <div className="text-left">
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 font-black">Today's Worksheet</div>
            <div className="text-sm font-bold text-white">{ws.title}</div>
          </div>
        </div>
        {result && <span className="text-xs font-black text-emerald-400" data-testid="ws-objective-score">Objective: {result.objective_score}%</span>}
      </button>

      {open && (
        /* paper worksheet */
        <div className="bg-white text-zinc-900 p-6 sm:p-8" data-testid="worksheet-paper">
          {/* Worksheet header */}
          <div className="border-b-2 border-zinc-900 pb-3 mb-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[10px] font-black tracking-[0.3em] uppercase text-orange-600">PRSVR Scholar Athlete Academy</div>
              <div className="flex gap-2">
                <button onClick={downloadPdf} disabled={downloading} className="flex items-center gap-1.5 border-2 border-zinc-900 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-zinc-900 hover:text-white transition-colors" data-testid="worksheet-download">
                  {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PDF
                </button>
              </div>
            </div>
            <h2 className="text-xl font-black mt-1">{ws.title}</h2>
            <div className="text-[11px] text-zinc-600 mt-1 flex flex-wrap gap-x-4">
              <span>Grade {ws.grade}</span><span>{ws.subject}</span><span>Standard {ws.standard_code}</span><span>{ws.date}</span>
            </div>
            {ws.instructions && <p className="text-[12px] text-zinc-700 mt-2"><span className="font-bold">Directions:</span> {ws.instructions}</p>}
          </div>

          {/* Today's Mini-Lesson — what the questions refer to */}
          {ws.mini_lesson && (
            <div className="mb-5 border-2 border-indigo-500 bg-indigo-50 p-4" data-testid="worksheet-mini-lesson">
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-4 h-4 text-indigo-700" />
                <span className="text-[11px] font-black uppercase tracking-[0.2em] text-indigo-700">Today's Mini-Lesson · read this first</span>
              </div>
              <p className="text-[13px] text-zinc-800 leading-relaxed">{ws.mini_lesson}</p>
              <p className="text-[10px] text-indigo-700/70 mt-2 italic">Use what you learned here to answer the questions below.</p>
            </div>
          )}

          {/* Part A — Multiple Choice */}
          {mc.length > 0 && (
            <div className="mb-6">
              <div className="text-[11px] font-black tracking-widest uppercase text-orange-600 mb-3">Part A · Multiple Choice</div>
              {mc.map((q) => (
                <div key={q.id} className="mb-4" data-testid={`ws-q-${q.id}`}>
                  <p className="text-sm font-medium"><span className="font-black">{num[q.id]}.</span> {q.prompt}</p>
                  <div className="mt-2 space-y-1.5 pl-5">
                    {(q.options || []).map((opt, i) => {
                      const val = LETTERS[i];
                      const sel = (answers[q.id] || '').toUpperCase() === val;
                      return (
                        <label key={i} className={`flex items-center gap-2.5 text-[13px] cursor-pointer ${result ? 'pointer-events-none' : ''}`}>
                          <input type="radio" name={q.id} disabled={!!result} checked={sel} onChange={() => setAns(q.id, val)} className="accent-orange-600 w-4 h-4" data-testid={`ws-${q.id}-opt-${val}`} />
                          <span>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                  <QResult q={q} />
                </div>
              ))}
            </div>
          )}

          {/* Part B — Fill in the blank */}
          {fb.length > 0 && (
            <div className="mb-6">
              <div className="text-[11px] font-black tracking-widest uppercase text-orange-600 mb-3">Part B · Fill In The Blank</div>
              {fb.map((q) => (
                <div key={q.id} className="mb-4" data-testid={`ws-q-${q.id}`}>
                  <p className="text-sm font-medium"><span className="font-black">{num[q.id]}.</span> {q.prompt}</p>
                  <input value={answers[q.id] || ''} disabled={!!result} onChange={(e) => setAns(q.id, e.target.value)} placeholder="Your answer" className="mt-2 ml-5 w-64 max-w-full border-b-2 border-zinc-400 focus:border-orange-600 outline-none text-sm py-1 bg-transparent disabled:opacity-70" data-testid={`ws-${q.id}-input`} />
                  <QResult q={q} />
                </div>
              ))}
            </div>
          )}

          {/* Part C — Problem solving */}
          {pr.length > 0 && (
            <div className="mb-2">
              <div className="text-[11px] font-black tracking-widest uppercase text-orange-600 mb-3">Part C · Problem Solving</div>
              {pr.map((q) => (
                <div key={q.id} className="mb-4" data-testid={`ws-q-${q.id}`}>
                  <p className="text-sm font-medium"><span className="font-black">{num[q.id]}.</span> {q.prompt}</p>
                  <textarea value={answers[q.id] || ''} disabled={!!result} onChange={(e) => setAns(q.id, e.target.value)} rows={4} placeholder="Show your work and explain your thinking…" className="mt-2 w-full border-2 border-zinc-300 focus:border-orange-600 outline-none text-sm p-2 rounded-sm disabled:opacity-70" data-testid={`ws-${q.id}-input`} />
                  <QResult q={q} />
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="mt-4 pt-4 border-t border-zinc-200 flex items-center gap-3">
            {!result ? (
              <button onClick={submit} disabled={submitting} className="flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:bg-zinc-400 text-white px-5 py-2.5 font-black text-xs tracking-[0.2em] uppercase transition-colors" data-testid="worksheet-submit">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Grading…</> : <><Sparkles className="w-4 h-4" /> Submit &amp; Check</>}
              </button>
            ) : (
              <button onClick={retry} className="flex items-center gap-2 border-2 border-zinc-900 px-5 py-2.5 font-black text-xs tracking-[0.2em] uppercase hover:bg-zinc-900 hover:text-white transition-colors" data-testid="worksheet-retry">
                <RefreshCw className="w-4 h-4" /> Try Again
              </button>
            )}
            {result && <span className="text-xs text-zinc-600 font-bold">Objective score: {result.objective_correct}/{result.objective_total} ({result.objective_score}%)</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyWorksheet;
